/**
 * Session service (skeleton).
 *
 * Wraps the durable server-side session that underpins resume. Shapes here are
 * local stand-ins for the shared `@contracts/api/*` schemas (see src/types).
 */

import type { FlowStepId, Regime, SessionChecksDTO } from '@/types';
import { API_BASE_URL, ApiError, delay, isMockMode, request } from './client';

/** Session config resolved from a magic-link invite token. */
export interface SessionConfig {
  sessionId: string;
  tenantId: string;
  regime: Regime;
  reference: string;
  customerName: string;
  /** Where to resume — the server is the source of truth for flow position. */
  currentStep: FlowStepId;
  completedSteps: FlowStepId[];
  /** Present only when the session was suspended and can be resumed. */
  resumeToken: string | null;
  agentId: string | null;
  agentName: string | null;
}

/**
 * Resolve the entry config from the REAL session read (17-Aug P0 RCA: the old
 * `GET /droom/session?invite=` route NEVER existed on the be — it only ever answered in mock
 * mode, and the first real-mode journey died here with a 401 on the flow's very first read).
 * `GET /sessions/{id}` is customer-bearer-readable (live-verified) and serves regime, branding,
 * the reference and the FSM state; the check plan drives the actual flow order separately.
 * Fail-soft: an unreadable entry read degrades to a minimal config — the flow proceeds and the
 * plan read (which is REQUIRED) is the real gate, so entry metadata never strands a customer.
 */
export async function loadSessionByInvite(
  inviteToken: string,
): Promise<SessionConfig> {
  if (isMockMode()) {
    await delay(280);
    return mockConfig(inviteToken);
  }
  interface SessionRead {
    sessionId?: string;
    vcipStatus?: string;
    regime?: Regime;
    externalIdentifier?: string | null;
    branding?: { name?: string | null } | null;
  }
  const fallback: SessionConfig = {
    sessionId: inviteToken,
    tenantId: '',
    regime: 'RBI' as Regime,
    reference: '',
    customerName: '',
    currentStep: 'welcome' as FlowStepId,
    completedSteps: [],
    resumeToken: null,
    agentId: null,
    agentName: null,
  };
  try {
    const dto = await request<SessionRead>(
      `/api/v1/sessions/${encodeURIComponent(inviteToken)}`,
    );
    const status = dto.vcipStatus ?? '';
    // The SERVER FSM is authoritative for the call band (AC1): a live/queued session re-enters
    // the call surface; everything else starts at welcome and the plan drives the order.
    const currentStep: FlowStepId =
      status === 'LIVE' || status === 'RESUMED' || status === 'RECONNECTING'
        ? ('call' as FlowStepId)
        : status === 'IN_QUEUE' || status === 'WARMING' || status === 'READY'
          ? ('waiting' as FlowStepId)
          : ('welcome' as FlowStepId);
    return {
      ...fallback,
      sessionId: dto.sessionId ?? inviteToken,
      tenantId: dto.branding?.name ?? '',
      regime: (dto.regime ?? 'RBI') as Regime,
      reference: dto.externalIdentifier ?? '',
      currentStep,
    };
  } catch {
    return fallback;
  }
}

/**
 * The vCIP_be resume projection (DroomResumeController.DroomResumeResponse) — data-minimised: just enough to
 * continue, no PII re-disclosure. `resumable`/`state` let the caller branch (rejoin vs "session ended") without
 * guessing; `currentStep` is a flow-step hint the client re-syncs against anyway.
 */
interface ResumeProjection {
  sessionId: string;
  tenantId: string;
  regime: Regime;
  currentStep: string; // "call" | "waiting" | "ended"
  resumable: boolean;
  state: string; // "RESUMABLE" | "ENDED"
}

/**
 * Resume a dropped session (PRD-SE-11/S7-8). In the INTERIM capability-URL model the resume token IS the session
 * id (the session link is the credential — same posture as the session-entry landing); real time-boxed resume
 * tokens pair with S6 customer auth, NOT this slice. Returns the SAME session id and (via the store) the SAME
 * completed steps + verifier affinity — a drop is never a restart. A non-resumable (terminal) session is an
 * honest "ended" that surfaces as a thrown {@link ApiError} so the caller can render the ended state.
 */
export async function resumeSession(
  resumeToken: string,
): Promise<SessionConfig> {
  if (isMockMode()) {
    await delay(280);
    return { ...mockConfig('resumed'), resumeToken };
  }
  const projection = await request<ResumeProjection>(`/api/v1/droom/session/resume`, {
    method: 'POST',
    body: { resumeToken },
  });
  if (!projection.resumable) {
    // The session is over — an honest terminal, not a fabricated resume. The caller routes to the state-aware
    // landing, which renders "session ended".
    throw new ApiError(410, 'This verification session has ended.');
  }
  // Map the minimal server truth into the flow's SessionConfig. The resume-critical persisted slice
  // (completedSteps, reference, customerName, verifier affinity) is preserved by the store on resume — it is
  // NOT re-disclosed by this capability read (data minimisation at the edge), so those fields are left blank
  // here and the caller applies ONLY the server-authoritative sessionId/tenant/regime/step.
  return {
    sessionId: projection.sessionId,
    tenantId: projection.tenantId,
    regime: projection.regime,
    reference: '',
    customerName: '',
    currentStep: projection.currentStep as FlowStepId,
    completedSteps: [],
    resumeToken,
    agentId: null,
    agentName: null,
  };
}

/**
 * The `/start` gap fix (KYCID-FSD-00013 §8) — read the FROZEN per-session check
 * plan so the flow order is pinned ONCE from the server's authority. On mock/
 * preview (or any read failure the caller catches) an EMPTY plan is returned,
 * which the store treats as today's legacy flow (the documented fail-safe). The
 * caller passes the result to `initCheckPlan`; a thrown error → safe-spine degrade.
 */
export async function fetchStartBootstrap(sessionId: string): Promise<SessionChecksDTO> {
  if (isMockMode()) {
    await delay(160);
    // Empty plan = today's flow (the fail-safe) — mock/preview is unchanged.
    return { sessionId, overallStatus: 'NOT_STARTED', plan: [], statuses: [] };
  }
  return request<SessionChecksDTO>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/checks`,
  );
}

/** Persist the customer's DPDP consent as a first-class, audit-bound artefact. */
export async function recordConsent(input: {
  sessionId: string;
  noticeVersion: string;
}): Promise<{ consentId: string; recordedAt: string }> {
  if (isMockMode()) {
    await delay(160);
    return { consentId: 'cns_mock', recordedAt: new Date().toISOString() };
  }
  // Server binds timestamp + server-observed IP + notice version, hash-chained
  // into the audit log (never trusts a client-sent IP — see client.ts).
  return request(`/api/v1/droom/consent`, { method: 'POST', body: input });
}

/* ── Session-entry landing (/session/:sessionId) — the joinability read ────────
 *
 * The landing tries the cleanest existing read, GET /api/v1/sessions/{id}
 * (vCIP_be SessionController). ACCESS POSTURE, verified against the checkout:
 * that route is OPERATOR-scoped — @PreAuthorize SCOPE_vcip.sessions.read — so
 * in production the unauthenticated droom gets 401 and this module degrades to
 * a STATE-AGNOSTIC landing. On the `local` profile (the deployed preview) the
 * PreviewOperatorFilter stamps every request with that scope, so the read
 * succeeds anonymously and returns the FULL SessionResponse (tenantPath,
 * resolverHash, snapshot ids…). We therefore parse ONLY `vcipStatus` and render
 * nothing else from the payload (data minimisation at the capability edge).
 *
 * NAMED DEFERRAL: a customer-safe session-summary endpoint (minimal projection,
 * capability/OTP-scoped auth) belongs in vCIP_be — NOT added in this slice.
 */

/**
 * What the front door may honestly claim about the session. Derived from the
 * backend's FSM-A `vcipStatus` (vCIP_be `VcipStatus`); every failure mode maps
 * to an explicit degrade state — a state is never fabricated client-side.
 */
export type Joinability =
  | 'joinable' // READY or the live band — the customer can join now
  | 'preparing' // INITIATED/SCHEDULED/IN_QUEUE/WARMING — keep the page open
  | 'ended' // FINALISING or a terminal state — over, for the customer
  | 'not_found' // no session behind this link (404; malformed non-UUID id → 400)
  | 'unknown'; // read denied (401/403) or unreachable — state-agnostic landing

export interface SessionEntrySummary {
  state: Joinability;
  /** The raw FSM-A value when — and only when — the read succeeded. */
  vcipStatus: string | null;
  /**
   * Additive (this release): the tenant branding block riding the session-entry
   * read. UNTRUSTED wire value — callers parse via `parseServedBranding`;
   * null when absent (older be) or when the read failed.
   */
  branding: unknown;
}

/**
 * What happens to the customer when the call is over (be V62, carried on the same
 * session read). SHOW_PAGE is both the default and the fail-safe: any doubt —
 * read failed, field absent, target invalid — ends on the platform page.
 * The redirect target comes from tenant configuration an admin authored, never
 * from anything on this wire the customer (or an attacker) could influence.
 */
export interface CompletionBehaviour {
  mode: 'SHOW_PAGE' | 'REDIRECT';
  /** Absolute https destination under REDIRECT; null otherwise. */
  redirectUrl: string | null;
  /** A name the customer will recognise; the server falls back to the URL host. */
  redirectLabel: string | null;
}

export const SHOW_PAGE: CompletionBehaviour = {
  mode: 'SHOW_PAGE',
  redirectUrl: null,
  redirectLabel: null,
};

/**
 * Parse the wire completion block, refusing anything that is not a well-formed
 * absolute https URL. The server already validates; the client re-checks because
 * the ONLY failure mode this screen is allowed is "stayed on our page".
 */
export function completionOf(raw: unknown): CompletionBehaviour {
  if (typeof raw !== 'object' || raw === null) return SHOW_PAGE;
  const c = raw as { mode?: unknown; redirectUrl?: unknown; redirectLabel?: unknown };
  if (c.mode !== 'REDIRECT' || typeof c.redirectUrl !== 'string') return SHOW_PAGE;
  try {
    const url = new URL(c.redirectUrl);
    if (url.protocol !== 'https:' || !url.hostname) return SHOW_PAGE;
    return {
      mode: 'REDIRECT',
      redirectUrl: url.toString(),
      redirectLabel:
        typeof c.redirectLabel === 'string' && c.redirectLabel.trim()
          ? c.redirectLabel
          : url.hostname,
    };
  } catch {
    return SHOW_PAGE;
  }
}

/**
 * Read the session's completion behaviour. NEVER throws; every failure is
 * SHOW_PAGE — a stranded customer is a support ticket, a mis-sent one is an
 * incident, and this function is only allowed the first of the two.
 */
export async function fetchCompletionBehaviour(
  sessionId: string,
): Promise<CompletionBehaviour> {
  if (isMockMode()) return SHOW_PAGE;
  try {
    const body = await request<{ completion?: unknown }>(
      `/api/v1/sessions/${encodeURIComponent(sessionId)}`,
    );
    return completionOf(body.completion);
  } catch {
    return SHOW_PAGE;
  }
}

/**
 * FSM-A states a customer may join — the LIVE BAND ONLY.
 *
 * READY deliberately is NOT here. It means the session finished warming, not that an officer took
 * the call, and the backend now refuses a customer join token until one has (an officer in an empty
 * room is not a call, and the recorder only arms on the LIVE transition). Offering a Join button at
 * READY would hand the customer a button that 400s — so READY is a PREPARING state on this screen,
 * where the page keeps polling and unlocks itself the moment an officer picks the session up.
 */
const JOINABLE_STATUSES = new Set([
  'LIVE',
  'RECONNECTING',
  'SUSPENDED',
  'RESUMED',
]);
/** Pre-live pipeline — the session exists but no officer is on it yet (READY included; see above). */
const PREPARING_STATUSES = new Set([
  'INITIATED',
  'SCHEDULED',
  'IN_QUEUE',
  'WARMING',
  'READY',
]);
/** Wrap-up + terminal — nothing left for the customer to join. */
const ENDED_STATUSES = new Set([
  'FINALISING',
  'SESSION_COMPLETE',
  'ABANDONED',
  'FAILED',
  // Terminal for the customer exactly like the others. Previously omitted, so a risk-rejected session
  // fell through to 'unknown' and rendered the state-agnostic landing instead of an honest ending.
  'RISK_REJECTED',
]);

/**
 * OWNER ITEM 26 — how a LIVE call has ENDED, according to the server.
 *
 * <p>`completed` = the officer finished the session (FINALISING / SESSION_COMPLETE): the customer
 * may be taken to the completion screen, and from there to the tenant's configured redirect.
 *
 * <p>`terminated` = the session is over WITHOUT a successful finish (ABANDONED / FAILED /
 * RISK_REJECTED). Deliberately a separate answer, because the completion screen carries a
 * "verified" pill — advancing a rejected or failed session onto it would tell a customer they
 * passed a check they did not. The droom shows the honest ending instead and says the RE will be in
 * touch; it does NOT narrate the decision, which is the RE's to deliver.
 *
 * <p>`null` = not an ending. Unknown values are never guessed at — an unrecognised status is not an
 * ending, and inventing one would end a live call on a wire value we do not understand.
 */
export type SessionEnding = 'completed' | 'terminated';

const COMPLETED_STATUSES = new Set(['FINALISING', 'SESSION_COMPLETE']);
const TERMINATED_STATUSES = new Set(['ABANDONED', 'FAILED', 'RISK_REJECTED']);

export function sessionEndingOf(vcipStatus: string | null | undefined): SessionEnding | null {
  if (!vcipStatus) return null;
  const s = vcipStatus.trim().toUpperCase();
  if (COMPLETED_STATUSES.has(s)) return 'completed';
  if (TERMINATED_STATUSES.has(s)) return 'terminated';
  return null;
}

/** Map a raw FSM-A status onto landing joinability. Unknown values are never guessed. */
export function joinabilityOf(vcipStatus: string | null | undefined): Joinability {
  if (!vcipStatus) return 'unknown';
  const s = vcipStatus.trim().toUpperCase();
  if (JOINABLE_STATUSES.has(s)) return 'joinable';
  if (PREPARING_STATUSES.has(s)) return 'preparing';
  if (ENDED_STATUSES.has(s)) return 'ended';
  return 'unknown';
}

const ENTRY_READ_TIMEOUT_MS = 8_000;

/**
 * Read the session's joinability for the landing. NEVER throws — every outcome
 * (including timeout) resolves to an honest {@link SessionEntrySummary}. The
 * result is ADVISORY for UX only: join enforcement stays server-side at the
 * media edge, so a failed read degrades the copy, not the customer's access.
 */
export async function fetchSessionEntrySummary(
  sessionId: string,
): Promise<SessionEntrySummary> {
  if (isMockMode()) {
    // Fixture state only — mock mode never calls the backend. LIVE, not READY: the demo should show
    // the joinable landing, and READY is no longer joinable (an officer must be on the call first).
    await delay(320);
    return { state: 'joinable', vcipStatus: 'LIVE', branding: null };
  }
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), ENTRY_READ_TIMEOUT_MS);
  try {
    // Actual BE wire shape is SessionResponse (`vcipStatus`); the OpenAPI
    // contract names the field `sessionState` — accept either, trust neither
    // beyond the status string.
    const body = await request<{
      vcipStatus?: string | null;
      sessionState?: string | null;
      branding?: unknown;
    }>(`/api/v1/sessions/${encodeURIComponent(sessionId)}`, {
      signal: controller.signal,
    });
    const raw = body.vcipStatus ?? body.sessionState ?? null;
    return { state: joinabilityOf(raw), vcipStatus: raw, branding: body.branding ?? null };
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 400)) {
      // 404: unknown id (opaque UUIDv7 — not an enumeration oracle).
      // 400: the id is not a UUID at all — an incomplete/mangled link.
      return { state: 'not_found', vcipStatus: null, branding: null };
    }
    // 401/403 (operator-scoped read, the prod posture), network, timeout.
    return { state: 'unknown', vcipStatus: null, branding: null };
  } finally {
    window.clearTimeout(timer);
  }
}

function mockConfig(seed: string): SessionConfig {
  return {
    sessionId: `sess_${seed}_7f3a`,
    tenantId: import.meta.env.VITE_DEFAULT_TENANT || 'hdfc-life',
    regime: 'RBI',
    reference: 'VCIP-7F3A-22K1',
    customerName: 'Priya',
    currentStep: 'welcome',
    completedSteps: [],
    resumeToken: null,
    agentId: null,
    agentName: null,
  };
}

export const usingRealBackend = (): boolean => !isMockMode() && !!API_BASE_URL;
